<template>
	<TopBanner
		:text="languageStore.getString('offline_no_connection')"
		:collapsed="!unchangingStore.offline"
		:offline="unchangingStore.offline" />
	<div class="console" :class="{'with-steps-sheet': funnelStore.showStepsBottomSheet, 'with-banner': unchangingStore.offline}">
		<SidebarMacro class="no-tablet" />
		<main>
			<router-view />
		</main>
	</div>
	<BusinessInfosPopup v-if="showBusinessInfoPopup" />
	<UnpaidInvoicesPopup v-if="showUnpaidInvoicesPopup" />
	<NotificationManager />
	<AssistantDrawer />
	<div id="user-email-anchor">{{ userEmail }}</div>
	<FunnelChoicePopup v-if="showFunnelChoicePopup && !showBusinessInfoPopup && !showUnpaidInvoicesPopup" @close="onFunnelChoiceClosed" />
	<StepsBottomSheet v-if="funnelStore.showStepsBottomSheet" ref="steps-bottom-sheet"/>
	<EndedFunnelPopup v-if="showEndedFunnelPopup && !showBusinessInfoPopup" :active="funnelStore.showEndedFunnelPopup" />
</template>

<script>
import AppNotification from "@/shared/components/AppNotification.vue";
import SidebarMacro from "@/console-new/components/shared/SidebarMacro.vue";
import NotificationManager from "@/console-new/components/shared/NotificationManager.vue";
import BusinessInfosPopup from "@/console-new/components/shared/BusinessInfosPopup.vue";
import EndedFunnelPopup from "@/console-new/components/shared/EndedFunnelPopup.vue";
import AssistantDrawer from "@/console-new/components/shared/AssistantDrawer.vue";

import langObject from "@/console-new/assets/json/langs.json";
import shopLangObject from "@/shop/assets/json/langs.json";
import posLangObject from "@/console-pos/assets/json/langs.json";
import langObjectAuto from "@/console-new/assets/json/auto.json";
import shopLangObjectAuto from "@/shop/assets/json/auto.json";
import posLangObjectAuto from "@/console-pos/assets/json/auto.json";

import PermissionsSectionCheck from "@/console-new/mixins/PermissionsSectionCheck";

import {useCookiesStore, useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore, useUnchangingStore, useMenuStore, useNativeStore, useFunnelStore} from "@/console-new/stores";
import * as Sentry from "@sentry/vue";
import StationPin from "@/console-new/components/shared/StationPin.vue";
import Hash from "@/shared/helpers/Hash";
import StepsBottomSheet from "@/console-new/components/shared/StepsBottomSheet.vue";
import FunnelChoicePopup from "@/console-new/components/shared/FunnelChoicePopup.vue";
import UnpaidInvoicesPopup from "@/console-new/components/shared/UnpaidInvoicesPopup.vue";
import TopBanner from "@/console-new/components/shared/TopBanner.vue";
import {getRouteInfo} from "@/console-new/helpers/permissions";
import {setParam, injectHotjar} from "@/shared/helpers/events";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const unchangingStore = useUnchangingStore();
		const menuStore = useMenuStore();
		const notificationStore = useNotificationStore();
		const funnelStore = useFunnelStore();
		const nativeStore = useNativeStore();
		const currencyStore = useCurrencyStore();
		const cookiesStore = useCookiesStore();

		return {languageStore, userStore, currencyStore, unchangingStore, funnelStore, menuStore, cookiesStore, showNotification: notificationStore.showNotification, nativeStore};
	},
	name: "App",
	components: {
		TopBanner,
		UnpaidInvoicesPopup, StepsBottomSheet, FunnelChoicePopup, EndedFunnelPopup, StationPin, SidebarMacro, AppNotification, BusinessInfosPopup, NotificationManager, AssistantDrawer},
	mixins: [PermissionsSectionCheck],
	data() {
		return {
			showEndedFunnelPopup: false,
			openingStepsSheet: false,
			funnelChoiceDismissed: !!localStorage.getItem('funnel-choice-' + this.userStore.currBusinessId),
		}
	},
	computed: {
		showBusinessInfoPopup() {
			return this.userStore.shouldFillFiscal && !this.userStore.dismissedFiscalPopup && this.$route.path !== '/lost';
		},
		showUnpaidInvoicesPopup() {
			return this.userStore.hasUnpaidInvoices && !this.userStore.dismissedUnpaidInvoicesPopup && this.$route.path !== '/lost';
		},
		showFunnelChoicePopup() {
			return !!this.funnelStore.showFunnelChoicePopup && !this.funnelChoiceDismissed;
		},
		userEmail() {
			return this.userStore.user && this.userStore.user.email ? this.userStore.user.email : '';
		}
	},
	methods: {
		onFunnelChoiceClosed() {
			this.funnelChoiceDismissed = true;
			if(this.funnelStore.showStepsBottomSheet) {
				this.openStepsBottomSheet();
			}
		},
		openStepsBottomSheet() {
			if (this.openingStepsSheet || this.showFunnelChoicePopup) { return }
			this.openingStepsSheet = true;
			this.$nextTick(() => {
				let targetElement = this.$refs['steps-bottom-sheet'];
				if(targetElement && !targetElement.activeStepsBottomSheet) {
					targetElement.activeStepsBottomSheet = true;
				}
				this.openingStepsSheet = false;
			});
		},
		collapseStepsBottomSheet() {
			let targetElement = this.$refs['steps-bottom-sheet'];
			if(targetElement && targetElement.activeStepsBottomSheet) {
				targetElement.activeStepsBottomSheet = false;
			}
		},
		funnelStepsChanged(stepA, stepB) {
			//one of them is null
			if((!stepA && !!stepB) || (!!stepA && !stepB)) {
				return true;
			}
			let checkingProperties = ['slotName', 'title', 'completed'];
			for(let i = 0; i < checkingProperties.length; i++) {
				let property = checkingProperties[i];
				if(stepA.hasOwnProperty(property) !== stepB.hasOwnProperty(property) || stepA[property] !== stepB[property]) {
					return true;
				}
			}
			return false;

		},
		setGtmUserData() {

			setParam('user_id', ""+this.userStore.user?.user_id);
			if(this.userStore.user?.email) {
				setParam('em', Hash.init().calculate(this.userStore.user.email.toLowerCase()) );
			}
			if(this.userStore.user?.phone) {
				let phone = this.userStore.user.phone.replace(/\D+/g, '');
				setParam('ph', Hash.init().calculate(phone));
			}

		}
	},
	created() {
		this.languageStore.addStrings(shopLangObject, "shop", shopLangObjectAuto);
		this.languageStore.addStrings(posLangObject, "pos", posLangObjectAuto);
		this.languageStore.addStrings(langObject, "console", langObjectAuto);

		document.addEventListener('dblclick', function(event) {
			event.preventDefault();
		}, { passive: false });

		this.$nextTick(() => {
			this.userStore.init();
			this.unchangingStore.init();
		});
		this.userStore.initDarkMode();
		setInterval(this.userStore.initDarkMode, 1000 * 60);

		// Store the current version
		if (typeof VERSION !== 'undefined') {
			sessionStorage.setItem('app_version', VERSION);
			console.log("Current version: "+VERSION);
		}

		this.cookiesStore.initConsent();

		// Check if user just completed signup verification
		if(!TEST && sessionStorage.getItem('new_signup') === '1') {
			setTimeout(() => {
				injectHotjar();
			}, 320);
		}

	},
	mounted() {
		let params = JSON.parse(sessionStorage.getItem('get_params'));
		if(params && params.message) {
			let message = params.message;
			delete params.message;
			sessionStorage.setItem('get_params', JSON.stringify(params));
			if(message === "stripe_failed") {
				this.showNotification("stripe-failed");
			} else if(message === "stripe_sub_base") {
				this.showNotification("stripe-sub-base");
			} else if(message === "stripe_subs_error") {
				this.showNotification("stripe-subs-error");
			} else if(message === "stripe_subs_success") {
				this.showNotification("stripe-subs-success");
			} else if(message === "shop_success") {
				this.showNotification("stripe-subs-success");
			} else if(message === "shop_fail") {
				this.showNotification("stripe-subs-error");
			}
		}

		this.nativeStore.refreshPreferences();
		this.funnelStore.init(()=>{
			setTimeout(()=>{
				this.funnelStore.canTransition = true;
			}, 500)
		});

	},
	watch: {
		'userStore.user.user_id': function (newVal) {
			if (newVal !== null) {
				this.setGtmUserData();
			}
		},
		'funnelStore.currStep'(newVal, oldVal) {
			if(!!newVal && !!oldVal && this.funnelStepsChanged(newVal, oldVal) && this.funnelStore.showStepsBottomSheet && !this.funnelStore.showEndedFunnelPopup) {
				this.openStepsBottomSheet();
			}
		},
		'funnelStore.showStepsBottomSheet'(newVal) {
			if(newVal) {
				this.openStepsBottomSheet();
			}
		},
		showFunnelChoicePopup(newVal) {
			if(newVal) {
				this.collapseStepsBottomSheet();
			}
		},
		'funnelStore.funnelChoice'() {
			if(!!this.funnelStore.funnelChoice) { return; }
			// cleared funnel choice, show initial popup
			this.funnelChoiceDismissed = !!localStorage.getItem('funnel-choice-' + this.userStore.currBusinessId);
		},
		'userStore.currBusinessColor'(newVal) {
			document.documentElement.style.setProperty('--business-color', newVal);
		},
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.funnelChoiceDismissed = !!localStorage.getItem('funnel-choice-' + newVal);
				this.userStore.refreshBusiness(this.userStore.currBusinessId, ()=>{
					this.currencyStore.initCurrency(this.userStore?.currBusiness?.country)
				});
				if(this.menuStore.loadedBusinessMenu) {
					this.menuStore.refreshMenusData();
				}
				this.funnelStore.init(()=>{
					setTimeout(()=>{
						this.funnelStore.canTransition = true;
					}, 500)
				});
				if(!LOCAL) {
					Sentry.setUser({ email: this.userStore.user.email });

					this.setGtmUserData();
					setParam('business_id', ""+this.userStore.currBusinessId);
				}
			}
		},
		'funnelStore.showEndedFunnelPopup': {
			handler(newVal) {
				if(!!newVal && !this.showEndedFunnelPopup) {
					this.showEndedFunnelPopup = true;
				}
			}, immediate: true
		},
	},
}
</script>

<style lang="scss">
:root {
	--business-color: var(--color-primary)
}
button, [type="button"], [type="reset"], [type="submit"] {
	-webkit-appearance: button;
	background-color: transparent;
	background-image: none;
}
div, span, p, h1, h2, h3, h4, h5, h6, a, button, label, select {
	@include noSelect();
}
.selectable {
	@include textSelect();
}
.vc-nav-items {
	.vc-nav-item {
		color: var(--txt-0);
	}
}
</style>
<style lang="scss" scoped>

.console {
	display: flex;
	align-items: stretch;
	height: 100%;
	transition: height 0.3s ease, filter .2s ease;

	&.with-banner {
		height: calc(100% - 40px);
	}
	&.with-steps-sheet {
		height: calc(100% - 55px);
		&.with-banner {
			height: calc(100% - 95px);
		}
	}
}

main {
	width: calc(100% - $sidebarMacroWidth);
	height: 100%;
	position: relative;
	background-color: var(--color-bg-primary-1);
}

@media (max-width: 920px) {
	.no-tablet {
		display: none;
	}
	main {
		width: 100%;
	}
}

#user-email-anchor {
	display: none;
	opacity: 0;
	visibility: hidden;
}

</style>